/**
 * Helper function to check if an object contains File objects
 */
function hasFiles(obj: any): boolean {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  // Check if it's a File object (but not a File object with a 'key' property which is from backend)
  if (obj instanceof File || (obj && typeof obj === 'object' && 'name' in obj && 'size' in obj && 'type' in obj && !('key' in obj))) {
    return true;
  }

  // Check if it's an array
  if (Array.isArray(obj)) {
    return obj.some((item) => hasFiles(item));
  }

  // Check all properties
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (hasFiles(obj[key])) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Recursively extract files from an object and replace them with null
 * Returns the modified object and a map of file paths
 */
function extractFiles(obj: any, map: Map<any, string[]>, path: string[] = []): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // Check if it's a File object (but not a File object with a 'key' property which is from backend)
  if (obj instanceof File || (obj && typeof obj === 'object' && 'name' in obj && 'size' in obj && 'type' in obj && !('key' in obj))) {
    map.set(obj, path);
    return null; // Placeholder, will be replaced in FormData
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item, index) => extractFiles(item, map, [...path, String(index)]));
  }

  // Handle objects
  const result: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      result[key] = extractFiles(obj[key], map, [...path, key]);
    }
  }

  return result;
}

/**
 * Build FormData for GraphQL multipart request (following graphql-upload spec)
 */
function buildFormData(operations: string, map: Map<any, string[]>, files: File[]): FormData {
  const formData = new FormData();
  
  // Add operations (query/mutation as JSON string)
  formData.append('operations', operations);
  
  // Build file map according to graphql-upload spec
  const fileMap: { [key: string]: string[] } = {};
  files.forEach((file, index) => {
    const paths = map.get(file);
    if (paths) {
      // Convert path array to GraphQL variable path format
      // e.g., ['createOwnershipInput', 'files', '0'] -> ['variables.createOwnershipInput.files.0']
      const variablePath = ['variables', ...paths].join('.');
      fileMap[String(index)] = [variablePath];
    }
  });
  
  // Add map
  formData.append('map', JSON.stringify(fileMap));
  
  // Add files
  files.forEach((file, index) => {
    formData.append(String(index), file);
  });
  
  return formData;
}

/**
 * Custom fetch function that handles multipart/form-data for GraphQL file uploads
 * This follows the graphql-upload specification
 */
export async function multipartFetch(
  url: string,
  options: RequestInit & { body?: string }
): Promise<Response> {
  // Parse the GraphQL request body
  let body: any;
  try {
    body = JSON.parse(options.body || '{}');
  } catch {
    return fetch(url, options);
  }

  const variables = body.variables || {};
  
  // Check if there are any File objects in the variables
  if (!hasFiles(variables)) {
    // No files, use regular JSON request
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
  }

  // Extract files from variables
  const fileMap = new Map<any, string[]>();
  const cleanedVariables = extractFiles(variables, fileMap);
  
  // Get all unique files
  const files: File[] = Array.from(fileMap.keys()).filter(
    (file) => file instanceof File || (file && typeof file === 'object' && 'name' in file && 'size' in file && !('key' in file))
  ) as File[];

  if (files.length === 0) {
    // No actual files found, use regular JSON
    return fetch(url, {
      ...options,
      body: JSON.stringify({
        ...body,
        variables: cleanedVariables,
      }),
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
  }

  // Build the operations JSON with cleaned variables (replace files with null)
  const operations = JSON.stringify({
    ...body,
    variables: cleanedVariables,
  });

  // Build FormData according to graphql-upload spec
  const formData = buildFormData(operations, fileMap, files);

  // Make multipart request
  const headers = new Headers(options.headers);
  // Remove Content-Type header - browser will set it with boundary for FormData
  headers.delete('Content-Type');

  return fetch(url, {
    ...options,
    method: options.method || 'POST',
    headers,
    body: formData,
  });
}

